<template>
  <div class="game">
    <table class="board">
      <tr :key="j" v-for="(letters, j) in rows">
        <td :class="letter.class" :style="letter.animationDelay ? `animation-delay: ${letter.animationDelay}ms` : ''" :key="i" v-for="(letter, i) in letters">{{ letter.value }}</td>
      </tr>
    </table>
    <div class="letter-data" v-if="state == 'PLAYING'">
      <div :class="'letter-row-' + i" :key="i" v-for="(row, i) in keyboardLayout()">
        <div class="letter" :key="letter" v-for="letter in row">
          <div v-if="letter == '@'" @click.prevent="play" class="enter-button">Enter</div>
          <div v-else-if="letter == '#'" @click.prevent="backspace" class="backspace-button">Delete</div>
          <div v-else :class="getLetterClass(letterData, letter)" @click.prevent="addLetter(letter)">{{ letter}}</div>
        </div>
      </div>
    </div>
    <div v-if="state == 'PLAYING'">
      <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>
    </div>
    <div class="win" v-else-if="state == 'WON'">
      <h1>Yay!</h1>
      <div class="new-game">
        <button @click.prevent="newGame" :disabled="animating">New Game</button>
      </div>
    </div>
    <div class="lose" v-else>
      <h1>Too bad!</h1>
      <div>The answer is {{answer}}</div>
      <div class="new-game">
        <button @click.prevent="newGame" :disabled="animating">New Game</button>
      </div>
    </div>
    <div class="post-game" v-if="state !== 'PLAYING'">
      <div class="summary-link">
        <a href="#" @click.prevent="toggleSummary">{{ showSummary ? 'Hide my stats' : 'Show my stats' }}</a>
      </div>
      <div class="summary" v-if="showSummary">
        <div v-if="summaryError" class="summary-error">{{ summaryError }}</div>
        <table v-else-if="summary" class="summary-table">
          <thead>
            <tr><th>Result</th><th>Count</th><th>%</th></tr>
          </thead>
          <tbody>
            <tr :key="n" v-for="n in 6">
              <td>Won in {{ n }} {{ n === 1 ? 'turn' : 'turns' }}</td>
              <td>{{ summary.wins[n] || 0 }}</td>
              <td>{{ percentOfTotal(summary.wins[n] || 0) }}</td>
            </tr>
            <tr>
              <td>Total Wins</td>
              <td>{{ summary.totalWins }}</td>
              <td>{{ percentOfTotal(summary.totalWins) }}</td>
            </tr>
            <tr>
              <td>Lost</td>
              <td>{{ summary.losses }}</td>
              <td>{{ percentOfTotal(summary.losses) }}</td>
            </tr>
            <tr class="summary-total">
              <td>Total Games</td>
              <td>{{ totalGames }}</td>
              <td>{{ percentOfTotal(totalGames) }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="summary-loading">Loading…</div>
        <div class="sync">
          <div v-if="linkedEmail">
            <span class="sync-status">Synced as {{ linkedEmail }}</span>
            <a href="#" class="sync-action" @click.prevent="unlinkDevice">Unlink this device</a>
          </div>
          <div v-else-if="linkStep === 'idle'">
            <a href="#" @click.prevent="linkStep = 'email'">Sync my stats across devices</a>
          </div>
          <form v-else-if="linkStep === 'email'" @submit.prevent="requestLinkCode">
            <input class="sync-input" type="email" v-model="linkEmail" placeholder="you@example.com" required :disabled="linkBusy" />
            <button type="submit" :disabled="linkBusy || !linkEmail">Send code</button>
          </form>
          <form v-else @submit.prevent="verifyLinkCode">
            <div class="sync-hint">We emailed a code to {{ linkEmail }}</div>
            <input class="sync-input" type="text" inputmode="numeric" pattern="[0-9]*" maxlength="6" v-model="linkCode" placeholder="6-digit code" required :disabled="linkBusy" />
            <button type="submit" :disabled="linkBusy || linkCode.length !== 6">Verify</button>
            <a href="#" class="sync-action" @click.prevent="linkStep = 'email'; linkCode = ''; linkError = null">Change email</a>
          </form>
          <div class="sync-error" v-if="linkError">{{ linkError }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// choose 'answers' from a list of common words
import commonWords from '../lib/common-words';
// but also allow guesses from an additional list
import otherWords from '../lib/other-words';
import { computeCells, getLetterClass, refreshLetterData } from '../lib/compute-cells';
export default {
  name: 'WordGame',
  props: {
    msg: String
  },
  data() {
      const br = this.blankRow();
      return {
        answer: this.randomDictionaryWord(),
        letterData: refreshLetterData(),
        guess: '',
        turn: 0,
        state: 'PLAYING',
        rows: [br, br, br, br, br, br],
        errorMessage: null,
        gameId: this.generateGameId(),
        userId: null,
        animating: false,
        showSummary: false,
        summary: null,
        summaryError: null,
        linkStep: 'idle',     // 'idle' | 'email' | 'code-sent'
        linkEmail: '',
        linkCode: '',
        linkError: null,
        linkBusy: false,
        linkedEmail: null
      }
  },
  mounted() {
      this.initializeUserId();
      this.linkedEmail = localStorage.getItem('word-game-linked-email');
      this.loadState();
      window.addEventListener('keydown', this.handleKeyPress);
  },
  beforeUnmount() {
      window.removeEventListener('keydown', this.handleKeyPress);
  },
  watch: {
    guess: function(s) {
        this.errorMessage = '';
        if (this.state === 'PLAYING' && this.turn < 6) {
            this.guess = s.toUpperCase();
            const row = `${this.guess}     `.slice(0,5).split('').map((value) => ({ value, class: 'guess'}));
            this.rows[this.turn] = row;
            this.saveState();
        }
        if (this.guess && this.guess.length > 5) {
            this.guess = this.guess.slice(0,5);
        }
    }
  },
  computed: {
    totalGames() {
        return this.summary ? this.summary.totalWins + this.summary.losses : 0;
    }
  },
  methods: {
      percentOfTotal(count) {
          if (!this.totalGames) return '0%';
          return `${Math.round((count / this.totalGames) * 100)}%`;
      },
      randomDictionaryWord() {
        const i = Math.floor(Math.random() * commonWords.length);
        return commonWords[i].toUpperCase();
      },
      setRow(n, guess, animate = false) {
        this.saveState();
        const params = { guess, answer: this.answer, letterData: this.letterData };
        const finalCells = computeCells(params);

        if (animate) {
          // Start with guess class and add flipping animation
          const animatingCells = finalCells.map((cell, i) => {
            return {
              value: cell.value,
              class: 'guess flipping',
              animationDelay: i * 125,
              finalClass: cell.class
            };
          });

          this.rows[n] = animatingCells;

          // Change color mid-flip for each tile (at 50% of animation = 375ms)
          animatingCells.forEach((cell, i) => {
            setTimeout(() => {
              // Create a new array to trigger Vue reactivity
              const updatedRow = [...this.rows[n]];
              updatedRow[i] = {
                ...updatedRow[i],
                class: `${cell.finalClass} flipping`
              };
              this.rows[n] = updatedRow;
            }, i * 125 + 375);
          });

          // Remove flipping class after animations complete
          setTimeout(() => {
            this.rows[n] = finalCells;
          }, 750 + (finalCells.length - 1) * 125);
        } else {
          this.rows[n] = finalCells;
        }
      },
      playDisabled() {
          return !this.guess || this.guess.length !== 5;
      },
      play() {
          // Both Enter clicks and key presses must respect the reveal lock.
          if (this.animating || this.state !== 'PLAYING') return;
          if (this.guess && this.guess.length === 5 ) {
            this.guess = this.guess.toUpperCase();
            if (this.validWord(this.guess)) {
              const currentGuess = this.guess.toUpperCase();
              const currentTurn = this.turn;

              this.animating = true;
              this.setRow(currentTurn, currentGuess, true);
              this.turn++;

              // Delay state updates until after animation completes
              setTimeout(() => {
                if (currentGuess === this.answer) {
                    this.state = 'WON';
                    // Trigger bounce animation for winning row
                    this.bounceWinningRow(currentTurn);
                    // Wait for bounce animation to complete before allowing new game
                    // Bounce takes: 4 tiles * 125ms stagger + 625ms animation = 1125ms
                    setTimeout(() => {
                      this.animating = false;
                    }, 1125);
                } else if (this.turn > 5) {
                  this.state = 'LOST';
                  this.animating = false;
                } else {
                  this.animating = false;
                }
                this.saveState();
                this.logPlay();
                this.guess = '';
                this.errorMessage = null;
              }, 750 + 4 * 125); // Wait for all tiles to flip
            } else {
              this.errorMessage = `${this.guess} is not in my dictionary`;
              this.saveState();
            }
          }
      },
      backspace() {
          if (this.animating || this.state !== 'PLAYING') return;
          if (this.guess && this.guess.length > 0) {
            this.guess = this.guess.slice(0, this.guess.length-1);
          }
      },
      validWord(w) {
          const lc = w.toLowerCase();
          return commonWords.indexOf(lc) >= 0 || otherWords.indexOf(lc) >= 0;
      },
      blankRow() {
          return [
              { value: ' ', class: 'blank' },
              { value: ' ', class: 'blank' },
              { value: ' ', class: 'blank' },
              { value: ' ', class: 'blank' },
              { value: ' ', class: 'blank' },
          ];
      },
      keyboardLayout() {
          return [
            'QWERTYUIOP',
            'ASDFGHJKL',
            '@ZXCVBNM#'
          ];
      },
      addLetter(letter) {
          if (this.animating || this.state !== 'PLAYING') return;
          this.guess = this.guess + letter;
      },
      saveState() {
          const state = JSON.stringify({
              guess: this.guess,
              turn: this.turn,
              answer: this.answer,
              letterData: this.letterData,
              state: this.state,
              rows: this.rows,
              gameId: this.gameId
          });
          localStorage.setItem('word-game-state', state);
      },
      logPlay() {
          const queryString = ['gameId', 'turn', 'guess', 'state', 'userId'].map(a => `${a}=${this[a]}`).join('&');
          const url = `/api/log?${queryString}`;
          fetch(url);
      },
      toggleSummary() {
          this.showSummary = !this.showSummary;
          if (this.showSummary) {
              this.loadSummary();
          }
      },
      async loadSummary() {
          this.summary = null;
          this.summaryError = null;
          try {
              const res = await fetch(`/api/summary?userId=${encodeURIComponent(this.userId)}`);
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              this.summary = await res.json();
          } catch (e) {
              this.summaryError = 'Could not load your stats. Please try again.';
          }
      },
      async requestLinkCode() {
          this.linkError = null;
          this.linkBusy = true;
          try {
              const res = await fetch('/api/link/request', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: this.linkEmail, userId: this.userId })
              });
              const body = await res.json().catch(() => ({}));
              if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
              this.linkStep = 'code-sent';
              this.linkCode = '';
          } catch (e) {
              this.linkError = e.message || 'Could not send the code. Please try again.';
          } finally {
              this.linkBusy = false;
          }
      },
      async verifyLinkCode() {
          this.linkError = null;
          this.linkBusy = true;
          try {
              const res = await fetch('/api/link/verify', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: this.linkEmail, code: this.linkCode, userId: this.userId })
              });
              const body = await res.json().catch(() => ({}));
              if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
              // This device adopts the account's canonical user id; future
              // plays and the stats query all use it from here on.
              localStorage.setItem('word-game-user-id', body.userId);
              localStorage.setItem('word-game-linked-email', this.linkEmail);
              this.userId = body.userId;
              this.linkedEmail = this.linkEmail;
              this.linkStep = 'idle';
              this.linkEmail = '';
              this.linkCode = '';
              this.loadSummary();
          } catch (e) {
              this.linkError = e.message || 'Could not verify the code. Please try again.';
          } finally {
              this.linkBusy = false;
          }
      },
      unlinkDevice() {
          // Back to an anonymous, device-local identity; account data is untouched.
          localStorage.removeItem('word-game-linked-email');
          const userId = crypto && crypto.randomUUID ? crypto.randomUUID() : 'user-' + Date.now();
          localStorage.setItem('word-game-user-id', userId);
          this.userId = userId;
          this.linkedEmail = null;
          this.linkStep = 'idle';
          this.linkError = null;
          this.loadSummary();
      },
      loadState() {
          try {
            const state = JSON.parse(localStorage.getItem('word-game-state'));
            this.answer = state.answer;
            this.guess = state.guess;
            this.letterData = state.letterData;
            this.rows = state.rows;
            this.state = state.state;
            this.turn = state.turn;
            this.gameId = state.gameId;
          } catch(e) {
            // ignore invalid state
          }
      },
      generateGameId() {
          return crypto && crypto.randomUUID ? crypto.randomUUID() : '1';
      },
      initializeUserId() {
          let userId = localStorage.getItem('word-game-user-id');
          if (!userId) {
              userId = crypto && crypto.randomUUID ? crypto.randomUUID() : 'user-' + Date.now();
              localStorage.setItem('word-game-user-id', userId);
          }
          this.userId = userId;
      },
      newGame() {
            const br = this.blankRow();
            this.answer = this.randomDictionaryWord();
            this.letterData = refreshLetterData();
            this.guess = '';
            this.turn = 0;
            this.state = 'PLAYING';
            this.rows = [br, br, br, br, br, br];
            this.errorMessage = null;
            this.gameId = this.generateGameId();
            this.showSummary = false;
            this.summary = null;
            this.summaryError = null;
            this.saveState();
      },
      focusInput() {
        this.$nextTick(() => {
          this.$el.querySelector('input').focus();
        });
      },
      getLetterClass,
      bounceWinningRow(rowIndex) {
          // Add bounce animation to each tile with staggered delays
          const row = [...this.rows[rowIndex]];
          row.forEach((cell, i) => {
            setTimeout(() => {
              const updatedRow = [...this.rows[rowIndex]];
              updatedRow[i] = {
                ...updatedRow[i],
                class: `${cell.class} bouncing`,
                animationDelay: 0
              };
              this.rows[rowIndex] = updatedRow;

              // Remove bouncing class after animation completes
              setTimeout(() => {
                const finalRow = [...this.rows[rowIndex]];
                finalRow[i] = {
                  ...finalRow[i],
                  class: cell.class,
                  animationDelay: 0
                };
                this.rows[rowIndex] = finalRow;
              }, 625);
            }, i * 125);
          });
      },
      handleKeyPress(event) {
          if (this.state !== 'PLAYING') {
              // Allow Ctrl+N/Command+N to start new game only when game is won or lost and not animating
              if ((event.metaKey || event.ctrlKey) && event.key.toUpperCase() === 'N' && (this.state === 'WON' || this.state === 'LOST') && !this.animating) {
                  this.newGame();
                  return;
              }
              return;
          }

          // Ignore all command or control key combinations during gameplay
          if (event.metaKey || event.ctrlKey) return;

          const key = event.key.toUpperCase();

          if (key === 'ENTER') {
              this.play();
          } else if (key === 'BACKSPACE') {
              this.backspace();
          } else if (/^[A-Z]$/.test(key)) {
              this.addLetter(key);
          }
      },
  }
}
</script>

<style scoped>
@keyframes flip {
  0% {
    transform: rotateX(0);
  }
  50% {
    transform: rotateX(90deg);
  }
  100% {
    transform: rotateX(0);
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

td {
  width: 64px;
  height: 64px;
  text-align: center;
  font-family: sans-serif;
  font-size: 24px;
  font-weight: bold;
  transform-style: preserve-3d;
}

td.flipping {
  animation: flip 0.75s ease-in-out;
}

td.bouncing {
  animation: bounce 0.625s ease-in-out;
}

div.correct,
td.correct {
  background-color: green;
  color: white;
}
div.close,
td.close {
  background-color: gold;
  color: white;
}
div.wrong,
td.wrong {
  background-color: gray;
  color: white;
}
td.guess {
  background-color: white;
  color: black;
  border: 1px solid black;
}
div.blank,
td.blank {
  background-color: white;
  color: white;
  border: 1px solid black;
}
div.letter {
  height: 60px;
  width: 38px;
  float: left;
  font-weight: bold;
}
div.unknown {
  background-color: #eaeaea;
  color: black;
}
div.correct, div.close, div.blank, div.unknown, div.wrong {
  margin: 2px;
  text-align: center;
  padding-top: 16px;
  height: 40px;
  border-radius: 5px;
}
button,
input {
  font-size: 24px;
}
input {
  width: 240px;
  margin-right: 10px;
}
form.guess-form {
  padding-top: 20px;
  padding-left: 40px;
}
.letter-row-0,
.letter-row-1,
.letter-row-2 {
  clear: left;
}
.letter-row-0 {
  padding-left: 0;
}
.letter-row-1 {
  padding-left: 14px;
}
.letter-row-2 {
  padding-left: 18px;
}
.letter-data {
  padding-top: 20px;
  padding-bottom: 20px;
  margin-left: 0;
  height: 150px;
}
.error-message {
  padding: 10px;
  background: #ffcccb;
  color: black;
  margin-top: 10px;
  border-radius: 5px;
  text-align: center;
}
form,
.board {
  padding-left: 10px;
}
.win, .lose, .post-game {
  text-align: center;
}
.backspace-button,
.enter-button {
  font-size: 11px;
  padding-top: 20px;
  padding-bottom: 20px;
}
.enter-button {
  padding-right: 4px;
}
.backspace-button {
  padding-left: 4px;
}
.form-link {
  font-size: 80%;
  text-align: center;
  padding-top: 20px;
}
.new-game {
  margin-top: 10px;
  text-align: center;
}
.play-button {
  border-radius: 10px;
  font-size: 16px;
  padding: 10px;
  border: none;
}
.new-game button {
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
  border: none;
}
.new-game button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.summary-link {
  margin-top: 12px;
  font-size: 14px;
}
.summary-link a {
  color: #1a73e8;
  cursor: pointer;
}
.summary {
  margin-top: 12px;
}
.summary-table {
  margin: 0 auto;
  border-collapse: collapse;
  font-size: 14px;
}
.summary-table th,
.summary-table td {
  border: 1px solid #ccc;
  padding: 6px 16px;
  width: auto;
  height: auto;
  text-align: left;
  font-weight: normal;
}
.summary-table th {
  background: #f2f2f2;
  font-weight: bold;
}
.summary-table td:nth-child(2),
.summary-table td:last-child {
  text-align: right;
}
.summary-total td {
  background: #f2f2f2;
  font-weight: bold;
}
.summary-error {
  color: #b00020;
  font-size: 14px;
}
.summary-loading {
  font-size: 14px;
  color: #666;
}
.sync {
  margin-top: 12px;
  font-size: 14px;
}
.sync a {
  color: #1a73e8;
  cursor: pointer;
}
.sync-status {
  color: #666;
}
.sync-action {
  margin-left: 8px;
}
.sync-hint {
  color: #666;
  margin-bottom: 6px;
}
.sync-input {
  font-size: 14px;
  width: 180px;
  padding: 6px;
  margin-right: 6px;
}
.sync button {
  font-size: 14px;
  border-radius: 10px;
  padding: 6px 10px;
  border: none;
}
.sync button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.sync-error {
  color: #b00020;
  margin-top: 6px;
}
</style>
